import { isPublicDemoRequested } from './dev-tauri-mock';

type DemoAction =
  | 'guardian-profile'
  | 'guardian-pin'
  | 'guardian-approvals'
  | 'guardian-approve'
  | 'guardian-signout'
  | 'child-profile'
  | 'child-pin'
  | 'child-reward'
  | 'child-money';

type DemoMessage = {
  type: 'chorepop-demo-action';
  action: DemoAction;
};

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function waitForElement(
  finder: () => HTMLElement | null,
  timeoutMilliseconds = 5_000,
): Promise<HTMLElement> {
  const started = Date.now();
  while (Date.now() - started < timeoutMilliseconds) {
    const element = finder();
    if (element) return element;
    await delay(50);
  }
  throw new Error('The guided demo could not find the next real app control.');
}

function buttonContaining(text: string): HTMLButtonElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLButtonElement>('button')).find((button) =>
      button.textContent?.replace(/\s+/g, ' ').trim().includes(text),
    ) ?? null
  );
}

function visibleButtonWithExactText(text: string): HTMLButtonElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLButtonElement>('button')).find(
      (button) =>
        button.textContent?.replace(/\s+/g, ' ').trim() === text &&
        !button.disabled &&
        button.getClientRects().length > 0,
    ) ?? null
  );
}

function profileButton(name: string): HTMLButtonElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLButtonElement>('.picker-tile')).find((button) =>
      button.textContent?.includes(name),
    ) ?? null
  );
}

function textElementContaining(text: string): HTMLElement | null {
  return (
    Array.from(document.querySelectorAll<HTMLElement>('[role="status"], [role="alert"]')).find(
      (element) => element.textContent?.replace(/\s+/g, ' ').trim().includes(text),
    ) ?? null
  );
}

function reportTarget(action: DemoAction, element: HTMLElement): void {
  const rect = element.getBoundingClientRect();
  window.parent.postMessage(
    {
      type: 'chorepop-demo-target',
      action,
      rect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      },
    },
    '*',
  );
}

async function pointThenClick(action: DemoAction, element: HTMLElement): Promise<void> {
  element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  await delay(250);
  reportTarget(action, element);
  await delay(650);
  element.click();
}

function setNativeInputValue(input: HTMLInputElement, value: string): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, value);
  input.dispatchEvent(
    new InputEvent('input', { bubbles: true, inputType: 'insertText', data: value }),
  );
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

async function performAction(action: DemoAction): Promise<void> {
  switch (action) {
    case 'guardian-profile': {
      const button = await waitForElement(() => profileButton('Alex'));
      await pointThenClick(action, button);
      break;
    }
    case 'guardian-pin':
    case 'child-pin': {
      const input = (await waitForElement(() =>
        document.querySelector<HTMLInputElement>('[data-testid="login-pin-input"]'),
      )) as HTMLInputElement;
      input.focus();
      setNativeInputValue(input, '123456');
      reportTarget(action, input);
      await delay(650);
      const submit = await waitForElement(() =>
        document.querySelector<HTMLButtonElement>('[data-testid="login-pin-submit"]'),
      );
      submit.click();
      break;
    }
    case 'guardian-approvals': {
      const button = await waitForElement(() => buttonContaining('Approvals'));
      await pointThenClick(action, button);
      break;
    }
    case 'guardian-approve': {
      const button = await waitForElement(() =>
        document.querySelector<HTMLButtonElement>(
          '.approval-decision-card--deposit .approval-approve-button',
        ),
      );
      await pointThenClick(action, button);
      await waitForElement(() => textElementContaining('Approved £5.00 for Sam.'));
      break;
    }
    case 'guardian-signout': {
      const accountMenu = await waitForElement(
        () =>
          document.querySelector<HTMLButtonElement>('[aria-label="Alex account menu"]') ??
          document.querySelector<HTMLButtonElement>('[data-testid="mobile-shell-menu"]'),
      );
      await pointThenClick(action, accountMenu);
      const signOut = await waitForElement(() => visibleButtonWithExactText('Sign out'));
      await pointThenClick(action, signOut);
      const confirm = await waitForElement(() => {
        const dialog = document.querySelector('[role="dialog"]');
        return (
          Array.from(dialog?.querySelectorAll<HTMLButtonElement>('button') ?? []).find(
            (button) => button.textContent?.trim() === 'Sign out',
          ) ?? null
        );
      });
      await pointThenClick(action, confirm);
      await waitForElement(() => profileButton('Sam'));
      break;
    }
    case 'child-profile': {
      const button = await waitForElement(() => profileButton('Sam'));
      await pointThenClick(action, button);
      break;
    }
    case 'child-reward': {
      await waitForElement(() =>
        document.querySelector<HTMLElement>(
          '.child-dashboard[data-reward-phase]:not([data-reward-phase="idle"])',
        ),
      );
      const target = await waitForElement(() =>
        document.querySelector<HTMLElement>('.child-reward-target'),
      );
      target.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
      reportTarget(action, target);
      await waitForElement(() =>
        document.querySelector<HTMLElement>('.child-reward-confirmation[role="status"]'),
      );
      await delay(1_400);
      break;
    }
    case 'child-money': {
      const button = await waitForElement(() => buttonContaining('Money'));
      await pointThenClick(action, button);
      break;
    }
  }

  await delay(600);
  window.parent.postMessage({ type: 'chorepop-demo-complete', action }, '*');
}

export function installPublicDemoGuide(): void {
  if (!isPublicDemoRequested()) return;

  window.addEventListener('message', (event: MessageEvent<DemoMessage>) => {
    if (event.source !== window.parent || event.data?.type !== 'chorepop-demo-action') return;
    void performAction(event.data.action).catch((error: unknown) => {
      window.parent.postMessage(
        {
          type: 'chorepop-demo-error',
          action: event.data.action,
          message: error instanceof Error ? error.message : 'The guided demo stopped.',
        },
        '*',
      );
    });
  });

  window.parent.postMessage({ type: 'chorepop-demo-ready' }, '*');
}
